import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import { useLocation } from "react-router-dom";

const API = "/backend/public/api";

const pageStyle = {
  display: "grid",
  gap: "22px",
};

const heroStyle = {
  background: "linear-gradient(135deg, #3f1820 0%, #7c2d3a 48%, #c2410c 100%)",
  color: "#fff",
  borderRadius: "18px",
  padding: "28px",
  boxShadow: "0 24px 54px rgba(78, 27, 36, 0.18)",
  overflow: "hidden",
};

const cardStyle = {
  background: "#ffffff",
  borderRadius: "14px",
  padding: "20px",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  boxShadow: "0 16px 34px rgba(15, 23, 42, 0.07)",
};

const tabWrapStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "10px",
};

const sectionTitleStyle = {
  margin: 0,
  color: "#0f172a",
  fontSize: "20px",
};

const labelStyle = {
  display: "block",
  marginBottom: "8px",
  color: "#475569",
  fontWeight: 700,
  fontSize: "13px",
};

const inputStyle = {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: "14px",
  padding: "12px 14px",
  color: "#0f172a",
  background: "#fff",
};

const textareaStyle = {
  ...inputStyle,
  minHeight: "96px",
  resize: "vertical",
};

const shortcodeBoxStyle = {
  background: "#ffffff",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  borderRadius: "14px",
  padding: "14px",
  display: "grid",
  gap: "12px",
  boxShadow: "0 12px 26px rgba(15, 23, 42, 0.06)",
};

const toolCardStyle = {
  background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
  border: "1px solid rgba(148, 163, 184, 0.14)",
  borderRadius: "14px",
  padding: "16px",
  display: "grid",
  gap: "10px",
};

const previewShellStyle = {
  width: "fit-content",
  maxWidth: "100%",
  justifySelf: "start",
  borderRadius: "14px",
  overflow: "hidden",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  background: "#fff",
  boxShadow: "0 16px 34px rgba(15, 23, 42, 0.08)",
};

const a4PreviewStyle = () => ({
  width: "620px",
  height: "877px",
  maxWidth: "none",
  margin: "0 auto",
  position: "relative",
  borderRadius: "18px",
  overflow: "hidden",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  boxShadow: "0 24px 50px rgba(15, 23, 42, 0.12)",
  background: "#fff",
});

const fieldCardStyle = {
  borderRadius: "12px",
  border: "1px solid rgba(148, 163, 184, 0.14)",
  background: "#f8fafc",
  padding: "14px",
  display: "grid",
  gap: "12px",
};

const controllerCardStyle = {
  borderRadius: "10px",
  border: "1px solid rgba(148, 163, 184, 0.14)",
  background: "#ffffff",
  padding: "10px",
  display: "grid",
  gap: "8px",
};

const heroMetricStyle = {
  border: "1px solid rgba(255,255,255,0.18)",
  background: "rgba(255,255,255,0.12)",
  borderRadius: "12px",
  padding: "12px",
  minWidth: "148px",
};

const pillStyle = {
  borderRadius: "999px",
  border: "1px solid rgba(148, 163, 184, 0.18)",
  background: "#f8fafc",
  color: "#475569",
  padding: "6px 10px",
  fontSize: "12px",
  fontWeight: 800,
};

const formatButtonStyle = (active) => ({
  border: active ? "none" : "1px solid rgba(148, 163, 184, 0.22)",
  background: active ? "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)" : "#fff",
  color: active ? "#fff" : "#334155",
  borderRadius: "10px",
  padding: "8px 10px",
  fontWeight: 800,
  fontSize: "12px",
  cursor: "pointer",
});

const docConfig = {
  admit: {
    title: "Admit Card Design",
    icon: "bi-person-badge-fill",
    tone: "rgba(219, 234, 254, 0.92)",
    description: "",
    sampleTitle: "AMCI Admit Card Sample",
    uploads: ["Background Image", "Top Logo", "Authority Signature"],
    controls: ["Header Height", "Student Block Width", "Exam Detail Spacing", "Signature Area Position"],
    shortcodes: ["{{student_name}}", "{{registration_no}}", "{{course_name}}", "{{exam_date}}", "{{exam_time}}", "{{study_centre}}", "{{session_year}}", "{{student_photo}}"],
  },
  marksheet: {
    title: "Marksheet Design",
    icon: "bi-file-earmark-bar-graph-fill",
    tone: "rgba(254, 240, 138, 0.78)",
    description: "",
    sampleTitle: "AMCI Marksheet Sample",
    uploads: ["Sample Background", "Board Logo", "Principal Signature", "Seal Image"],
    controls: ["Student Info Position", "Semester Table Width", "Grade Panel Alignment", "Footer Height"],
    shortcodes: ["{{student_name}}", "{{registration_no}}", "{{course_name}}", "{{period_of_course}}", "{{admission_date}}", "{{course_completion_date}}", "{{study_centre}}", "{{centre_name}}", "{{centre_code}}", "{{student_photo}}", "{{qr_code}}", "{{module_details}}", "{{total_obtained}}", "{{theory_full_marks}}", "{{practical_full_marks}}", "{{project_assignment_obtained_marks}}", "{{project_assignment_full_marks}}", "{{project_assignment_pass_marks}}", "{{viva_full_marks}}", "{{total_full_marks}}", "{{theory_pass_marks}}", "{{practical_pass_marks}}", "{{viva_pass_marks}}", "{{total_pass_marks}}", "{{percentage}}", "{{grade}}", "{{obtained_marks}}", "{{issue_date}}"],
  },
  certificate: {
    title: "Certificate Design",
    icon: "bi-award-fill",
    tone: "rgba(220, 252, 231, 0.92)",
    description: "",
    sampleTitle: "AMCI Certificate Sample",
    uploads: ["Certificate Background", "Institute Logo", "Seal Image", "Chairman Signature"],
    controls: ["Title Position", "Student Name Focus", "Certificate Body Width", "Serial Footer Position"],
    shortcodes: ["{{student_name}}", "{{father_name}}", "{{registration_no}}", "{{course_name}}", "{{period_of_course}}", "{{admission_date}}", "{{course_completion_date}}", "{{centre_name}}", "{{centre_code}}", "{{centre_address}}", "{{student_photo}}", "{{qr_code}}", "{{grade}}", "{{grade_point}}", "{{certificate_no}}", "{{issue_date}}", "{{session_year}}"],
  },
  idcard: {
    title: "ID Card Design",
    icon: "bi-person-vcard-fill",
    tone: "rgba(255, 237, 213, 0.92)",
    description: "",
    sampleTitle: "AMCI ID Card Sample",
    uploads: ["Front Background", "Back Background", "Institute Logo"],
    controls: ["Front Photo Area", "Front Text Spacing", "Back Address Block", "QR Position"],
    shortcodes: ["{{student_name}}", "{{registration_no}}", "{{course_name}}", "{{valid_upto}}", "{{student_photo}}", "{{student_mobile}}", "{{centre_code}}", "{{barcode_value}}"],
  },
};

const franchiseAffiliatedUploadLabels = [
  "Header Image",
  "Franchise Authority Signature",
  "NIDCS Administrator Signature",
];

const adjustableFieldConfig = {
  marksheet: [
    { key: "student_name", label: "Student Name", placeholder: "{{student_name}}" },
    { key: "registration_no", label: "Registration No", placeholder: "{{registration_no}}" },
    { key: "course_name", label: "Course Name", placeholder: "{{course_name}}" },
    { key: "period_of_course", label: "Period of Course", placeholder: "{{period_of_course}}" },
    { key: "study_centre", label: "Study Centre", placeholder: "{{study_centre}}" },
    { key: "centre_code", label: "Centre Code", placeholder: "{{centre_code}}" },
    { key: "student_photo", label: "Student Photo", placeholder: "{{student_photo}}" },
    { key: "qr_code", label: "QR Code", placeholder: "{{qr_code}}" },
    { key: "module_details", label: "Module Details", placeholder: "{{module_details}}" },
    { key: "total_obtained", label: "Total Obtained", placeholder: "{{total_obtained}}" },
    { key: "theory_full_marks", label: "Theory Full Marks", placeholder: "{{theory_full_marks}}" },
    { key: "theory_pass_marks", label: "Theory Pass Marks", placeholder: "{{theory_pass_marks}}" },
    { key: "obtained_marks", label: "Obtained Marks", placeholder: "{{obtained_marks}}" },
    { key: "practical_obtained_marks", label: "Practical Obtained", placeholder: "{{practical_obtained_marks}}" },
    { key: "practical_full_marks", label: "Practical Full", placeholder: "{{practical_full_marks}}" },
    { key: "practical_pass_marks", label: "Practical Pass", placeholder: "{{practical_pass_marks}}" },
    { key: "project_assignment_obtained_marks", label: "Project / Assignment Obtained", placeholder: "{{project_assignment_obtained_marks}}" },
    { key: "project_assignment_full_marks", label: "Project / Assignment Full", placeholder: "{{project_assignment_full_marks}}" },
    { key: "project_assignment_pass_marks", label: "Project / Assignment Pass", placeholder: "{{project_assignment_pass_marks}}" },
    { key: "viva_obtained_marks", label: "Viva Obtained", placeholder: "{{viva_obtained_marks}}" },
    { key: "viva_full_marks", label: "Viva Full", placeholder: "{{viva_full_marks}}" },
    { key: "viva_pass_marks", label: "Viva Pass", placeholder: "{{viva_pass_marks}}" },
    { key: "total_full_marks", label: "Total Full Marks", placeholder: "{{total_full_marks}}" },
    { key: "total_pass_marks", label: "Total Pass Marks", placeholder: "{{total_pass_marks}}" },
    { key: "percentage", label: "Percentage", placeholder: "{{percentage}}" },
    { key: "grade", label: "Grade", placeholder: "{{grade}}" },
    { key: "issue_date", label: "Issue Date", placeholder: "{{issue_date}}" },
  ],
  certificate: [
    { key: "student_name", label: "Student Name", placeholder: "{{student_name}}" },
    { key: "father_name", label: "Father Name", placeholder: "{{father_name}}" },
    { key: "registration_no", label: "Registration No", placeholder: "{{registration_no}}" },
    { key: "student_photo", label: "Student Photo", placeholder: "{{student_photo}}" },
    { key: "qr_code", label: "QR Code", placeholder: "{{qr_code}}" },
    { key: "body_text", label: "Body Text", placeholder: "This is to certify that {{student_name}} has successfully completed {{course_name}}..." },
    { key: "course_name", label: "Course Name", placeholder: "{{course_name}}" },
    { key: "period_of_course", label: "Period of Course", placeholder: "{{period_of_course}}" },
    { key: "grade", label: "Grade", placeholder: "{{grade}}" },
    { key: "grade_point", label: "Grade Point", placeholder: "{{grade_point}}" },
    { key: "certificate_no", label: "Certificate No", placeholder: "{{certificate_no}}" },
    { key: "issue_date", label: "Issue Date", placeholder: "{{issue_date}}" },
    { key: "session_year", label: "Session", placeholder: "{{session_year}}" },
    { key: "footer_note", label: "Footer Note", placeholder: "Authorised and digitally verifiable" },
  ],
};

const initialPreviewFieldValues = {
  marksheet: {
    student_name: "{{student_name}}",
    registration_no: "{{registration_no}}",
    course_name: "{{course_name}}",
    period_of_course: "Period of Course: {{period_of_course}}",
    study_centre: "{{study_centre}}",
    centre_code: "{{centre_code}}",
    student_photo: "{{student_photo}}",
    qr_code: "{{qr_code}}",
    module_details: "{{module_details}}",
    total_obtained: "{{total_obtained}}",
    obtained_marks: "{{obtained_marks}}",
    theory_full_marks: "{{theory_full_marks}}",
    theory_pass_marks: "{{theory_pass_marks}}",
    practical_obtained_marks: "{{practical_obtained_marks}}",
    practical_full_marks: "{{practical_full_marks}}",
    practical_pass_marks: "{{practical_pass_marks}}",
    project_assignment_obtained_marks: "{{project_assignment_obtained_marks}}",
    project_assignment_full_marks: "{{project_assignment_full_marks}}",
    project_assignment_pass_marks: "{{project_assignment_pass_marks}}",
    viva_obtained_marks: "{{viva_obtained_marks}}",
    viva_full_marks: "{{viva_full_marks}}",
    viva_pass_marks: "{{viva_pass_marks}}",
    total_full_marks: "{{total_full_marks}}",
    total_pass_marks: "{{total_pass_marks}}",
    percentage: "{{percentage}}",
    grade: "{{grade}}",
    issue_date: "{{issue_date}}",
  },
  certificate: {
    title: "Affiliation Certificate",
    certify_line: "This is to certify that",
    franchise_name: "{{centre_name}}",
    affiliation_line: "is affiliated with NIDCS as an authorized franchise centre.",
    centre_code_label: "Centre Code",
    centre_code_value: "{{centre_code}}",
    category_label: "Category",
    category_value: "{{course_name}}",
    location_label: "Location",
    location_value: "{{centre_address}}",
    authority_label: "Franchise Authority",
    admin_label: "NIDCS Administrator",
    student_name: "{{student_name}}",
    father_name: "{{father_name}}",
    registration_no: "{{registration_no}}",
    student_photo: "{{student_photo}}",
    qr_code: "{{qr_code}}",
    body_text: "This is to certify that {{student_name}} has successfully completed the course {{course_name}} with grade {{grade}} during {{period_of_course}}.",
    course_name: "{{course_name}}",
    period_of_course: "Period of Course: {{period_of_course}}",
    grade: "{{grade}}",
    grade_point: "{{grade_point}}",
    certificate_no: "{{certificate_no}}",
    issue_date: "{{issue_date}}",
    session_year: "{{session_year}}",
    footer_note: "Authorised and digitally verifiable",
  },
};

const initialPreviewFieldPositions = {
  marksheet: {
    title: { x: 210, y: 72 },
    student_name: { x: 76, y: 170 },
    registration_no: { x: 76, y: 228 },
    course_name: { x: 76, y: 286 },
    period_of_course: { x: 76, y: 326 },
    study_centre: { x: 76, y: 354 },
    centre_code: { x: 408, y: 354 },
    student_photo: { x: 610, y: 150 },
    qr_code: { x: 620, y: 330 },
    module_details: { x: 76, y: 396 },
    total_obtained: { x: 76, y: 902 },
    obtained_marks: { x: 76, y: 858 },
    theory_full_marks: { x: 244, y: 790 },
    theory_pass_marks: { x: 408, y: 790 },
    practical_obtained_marks: { x: 76, y: 812 },
    practical_full_marks: { x: 244, y: 812 },
    practical_pass_marks: { x: 408, y: 812 },
    project_assignment_obtained_marks: { x: 76, y: 834 },
    project_assignment_full_marks: { x: 244, y: 834 },
    project_assignment_pass_marks: { x: 408, y: 834 },
    viva_obtained_marks: { x: 76, y: 878 },
    viva_full_marks: { x: 244, y: 878 },
    viva_pass_marks: { x: 408, y: 878 },
    total_full_marks: { x: 244, y: 902 },
    total_pass_marks: { x: 408, y: 902 },
    percentage: { x: 530, y: 858 },
    grade: { x: 330, y: 902 },
    issue_date: { x: 530, y: 902 },
  },
  certificate: {
    title: { x: 290, y: 220 },
    certify_line: { x: 360, y: 300 },
    franchise_name: { x: 250, y: 356 },
    affiliation_line: { x: 180, y: 414 },
    centre_code_label: { x: 90, y: 488 },
    centre_code_value: { x: 300, y: 488 },
    category_label: { x: 90, y: 540 },
    category_value: { x: 300, y: 540 },
    location_label: { x: 90, y: 592 },
    location_value: { x: 300, y: 592 },
    authority_label: { x: 110, y: 800 },
    admin_label: { x: 700, y: 800 },
    student_name: { x: 170, y: 230 },
    father_name: { x: 170, y: 292 },
    registration_no: { x: 740, y: 610 },
    student_photo: { x: 930, y: 220 },
    qr_code: { x: 930, y: 380 },
    body_text: { x: 170, y: 348 },
    course_name: { x: 170, y: 540 },
    period_of_course: { x: 170, y: 572 },
    grade: { x: 170, y: 602 },
    grade_point: { x: 352, y: 602 },
    certificate_no: { x: 740, y: 650 },
    issue_date: { x: 170, y: 650 },
    session_year: { x: 420, y: 650 },
    footer_note: { x: 340, y: 720 },
  },
};

const buildBlankCertificatePreviewValues = () => {
  const base = deepClone(initialPreviewFieldValues.certificate);
  return Object.keys(base).reduce((acc, key) => {
    acc[key] = "";
    return acc;
  }, {});
};

const franchiseAffiliatedTextFields = [
  { key: "title", label: "Certificate Title" },
  { key: "certify_line", label: "Certify Line" },
  { key: "franchise_name", label: "Franchise Name" },
  { key: "affiliation_line", label: "Affiliation Line" },
  { key: "centre_code_label", label: "Centre Code Label" },
  { key: "centre_code_value", label: "Centre Code Value" },
  { key: "category_label", label: "Category Label" },
  { key: "category_value", label: "Category Value" },
  { key: "location_label", label: "Location Label" },
  { key: "location_value", label: "Location Value" },
  { key: "authority_label", label: "Authority Label" },
  { key: "admin_label", label: "Administrator Label" },
];

const franchiseAffiliatedShortcodes = [
  "{{title}}",
  "{{certify_line}}",
  "{{franchise_name}}",
  "{{affiliation_line}}",
  "{{centre_code_label}}",
  "{{centre_code_value}}",
  "{{category_label}}",
  "{{category_value}}",
  "{{location_label}}",
  "{{location_value}}",
  "{{authority_label}}",
  "{{admin_label}}",
];

const shortcodeKeyMap = {
  student_name: "{{student_name}}",
  father_name: "{{father_name}}",
  student_photo: "{{student_photo}}",
  qr_code: "{{qr_code}}",
  registration_no: "{{registration_no}}",
  course_name: "{{course_name}}",
  admission_date: "{{admission_date}}",
  course_completion_date: "{{course_completion_date}}",
  period_of_course: "{{period_of_course}}",
  study_centre: "{{study_centre}}",
  centre_name: "{{centre_name}}",
  centre_code: "{{centre_code}}",
  centre_address: "{{centre_address}}",
  module_details: "{{module_details}}",
  total_obtained: "{{total_obtained}}",
  obtained_marks: "{{obtained_marks}}",
  theory_full_marks: "{{theory_full_marks}}",
  theory_pass_marks: "{{theory_pass_marks}}",
  practical_obtained_marks: "{{practical_obtained_marks}}",
  practical_full_marks: "{{practical_full_marks}}",
  practical_pass_marks: "{{practical_pass_marks}}",
  project_assignment_obtained_marks: "{{project_assignment_obtained_marks}}",
  project_assignment_full_marks: "{{project_assignment_full_marks}}",
  project_assignment_pass_marks: "{{project_assignment_pass_marks}}",
  viva_obtained_marks: "{{viva_obtained_marks}}",
  viva_full_marks: "{{viva_full_marks}}",
  viva_pass_marks: "{{viva_pass_marks}}",
  total_full_marks: "{{total_full_marks}}",
  total_pass_marks: "{{total_pass_marks}}",
  percentage: "{{percentage}}",
  grade: "{{grade}}",
  grade_point: "{{grade_point}}",
  issue_date: "{{issue_date}}",
  certificate_no: "{{certificate_no}}",
  session_year: "{{session_year}}",
  footer_note: "{{footer_note}}",
};

const createDefaultFormState = () => ({
  headerTitle: "",
  subTitle: "",
  studentNameCode: "{{student_name}}",
  bodyText: "",
  footerNote: "",
  certificateBodyStyle: {
    fontFamily: "Georgia, serif",
    fontSize: 20,
    bold: false,
    italic: false,
    underline: false,
    bodyWidth: 760,
  },
  certificateBorderStyle: {
    enabled: true,
    width: 2,
    style: "solid",
    color: "#0f172a",
    radius: 0,
    padding: 0,
    margin: 0,
    innerEnabled: false,
    innerWidth: 1,
    innerStyle: "solid",
    innerColor: "#7c2d3a",
    innerGap: 10,
    innerRadius: 0,
  },
  certificateFieldStyleMap: {},
  certificateImageLayout: {
    headerImage: { x: 150, y: 48, width: 320, height: 110, fit: "contain", cropX: 50, cropY: 50 },
    authoritySignature: { x: 70, y: 746, width: 150, height: 58, fit: "contain", cropX: 50, cropY: 50 },
    adminSignature: { x: 400, y: 746, width: 150, height: 58, fit: "contain", cropX: 50, cropY: 50 },
  },
  uploads: {},
});

const deepClone = (value) => JSON.parse(JSON.stringify(value));

const removeCertificateSerialField = (value) => {
  if (!value || typeof value !== "object") return value;
  const copy = { ...value };
  delete copy.verification_qr;
  return copy;
};

const removedMarksheetFields = [
  "roll_no",
  "semester_rows",
  "total_marks",
  "full_marks",
  "pass_marks",
  "grade_point",
  "footer_note",
  "project_obtained_marks",
  "project_full_marks",
  "project_pass_marks",
];

const removeHiddenStructuredFields = (documentType, value) => {
  if (!value || typeof value !== "object") return value;
  const copy = documentType === "certificate" ? removeCertificateSerialField(value) : { ...value };
  if (documentType === "certificate") {
    delete copy.institute_name;
    delete copy.institute_subtitle;
  }
  if (documentType === "marksheet") {
    delete copy.title;
    delete copy.study_centre_code;
    removedMarksheetFields.forEach((key) => {
      delete copy[key];
    });
  }
  return copy;
};

const imageSizeLimit = (label) => {
  const isBackground = /background|sample/i.test(label || "");
  return isBackground ? { width: 1000, height: 720, quality: 0.62 } : { width: 420, height: 420, quality: 0.68 };
};

const isBackgroundUpload = (label) => /background|sample/i.test(label || "");

const loadImage = (source) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = source;
  });

const fileToObjectUrl = (file) =>
  new Promise((resolve, reject) => {
    try {
      resolve(URL.createObjectURL(file));
    } catch (error) {
      reject(error);
    }
  });

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

async function compressImageSource(source, label) {
  if (!source) return "";

  const image = await loadImage(source);
  const limit = imageSizeLimit(label);
  const ratio = Math.min(1, limit.width / image.width, limit.height / image.height);
  const width = Math.max(1, Math.round(image.width * ratio));
  const height = Math.max(1, Math.round(image.height * ratio));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  context.drawImage(image, 0, 0, width, height);

  let output = canvas.toDataURL("image/webp", limit.quality);
  if (output.length > 240000) {
    const fallbackRatio = Math.min(1, 700 / width, 500 / height);
    canvas.width = Math.max(1, Math.round(width * fallbackRatio));
    canvas.height = Math.max(1, Math.round(height * fallbackRatio));
    canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
    output = canvas.toDataURL("image/webp", 0.52);
  }

  return output;
}

async function compressImageFile(file, label) {
  if (isBackgroundUpload(label)) {
    return fileToDataUrl(file);
  }

  const objectUrl = await fileToObjectUrl(file);
  try {
    return await compressImageSource(objectUrl, label);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function compressUploads(uploads) {
  const entries = await Promise.all(
    Object.entries(uploads || {}).map(async ([label, value]) => {
      if (isBackgroundUpload(label)) return [label, value];
      return String(value || "").startsWith("data:image/") ? [label, await compressImageSource(value, label)] : [label, value];
    })
  );
  return Object.fromEntries(entries);
}

const sampleShortcodeValues = {
  student_name: "Rahul Sharma",
  father_name: "Sanjay Sharma",
  student_photo: "Student Photo",
  qr_code: "QR Code",
  registration_no: "AMCI/2026/00124",
  course_name: "Diploma in Computer Application",
  study_centre: "AMCI Skill Centre",
  centre_name: "AMCI Skill Centre",
  centre_code: "AMCI-001",
  centre_address: "Jorehira, Jhantipahari, Bankura, West Bengal",
  module_details: "Module 1: 86/100\nModule 2: 91/100\nModule 3: 88/100",
  total_obtained: "370",
  obtained_marks: "265",
  theory_full_marks: "300",
  theory_pass_marks: "90",
  practical_obtained_marks: "45",
  practical_full_marks: "50",
  practical_pass_marks: "15",
  project_assignment_obtained_marks: "42",
  project_assignment_full_marks: "50",
  project_assignment_pass_marks: "15",
  viva_obtained_marks: "18",
  viva_full_marks: "20",
  viva_pass_marks: "6",
  total_full_marks: "420",
  total_pass_marks: "126",
  percentage: "88.33%",
  grade: "A+",
  grade_point: "9.00",
  issue_date: "24 Apr 2026",
  certificate_no: "CERT-AMCI-00124",
  session_year: "2025-2026",
  footer_note: "Authorised and digitally verifiable",
};

const shortcodeToKey = (shortcode) => String(shortcode || "").replace(/[{}]/g, "").trim();

const resolvePreviewShortcodes = (text, previewFields) => {
  if (!text) return "";

  return Object.entries(shortcodeKeyMap).reduce((output, [fieldKey, shortcode]) => {
    const savedValue = previewFields[fieldKey];
    const replacement = savedValue && savedValue !== shortcode ? savedValue : sampleShortcodeValues[fieldKey] || shortcode;
    return output.split(shortcode).join(replacement);
  }, text);
};

function TabButton({ active, icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: active ? "1px solid rgba(124, 45, 58, 0.16)" : "1px solid rgba(148, 163, 184, 0.18)",
        background: active ? "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)" : "#ffffff",
        color: active ? "#ffffff" : "#4e1b24",
        borderRadius: "12px",
        padding: "13px 14px",
        fontWeight: 800,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        minHeight: "48px",
        boxShadow: active ? "0 14px 26px rgba(124, 45, 58, 0.18)" : "0 10px 20px rgba(15, 23, 42, 0.04)",
        cursor: "pointer",
      }}
    >
      <i className={`bi ${icon}`}></i>
      {label}
    </button>
  );
}

function UploadTool({ label, preview, onChange }) {
  return (
    <div style={toolCardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center" }}>
        <div style={{ color: "#0f172a", fontWeight: 800 }}>{label}</div>
        <span style={{ ...pillStyle, padding: "4px 8px" }}>{preview ? "Ready" : "Empty"}</span>
      </div>
      <input type="file" accept=".png,.jpg,.jpeg,.webp" style={inputStyle} onChange={onChange} />
      {preview ? (
        <div style={{ borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(148, 163, 184, 0.16)", background: "#fff" }}>
          <img src={preview} alt={label} style={{ width: "100%", height: "130px", objectFit: isBackgroundUpload(label) ? "contain" : "cover", display: "block", background: "#fff" }} />
        </div>
      ) : null}
      <div style={{ color: "#64748b", fontSize: "12px", lineHeight: 1.6 }}>PNG, JPG, WEBP upload korlei preview instantly update hobe.</div>
    </div>
  );
}

function ShortcodeList({ items, previewValues = sampleShortcodeValues }) {
  const [copiedItem, setCopiedItem] = useState("");

  const handleCopy = async (item) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(item);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = item;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopiedItem(item);
      window.setTimeout(() => setCopiedItem(""), 1800);
    } catch (error) {
      setCopiedItem("");
    }
  };

  return (
    <div style={shortcodeBoxStyle}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", flexWrap: "wrap" }}>
        <div style={{ color: "#7c2d3a", fontWeight: 800, fontSize: "13px" }}>Short Code Reference</div>
        <div style={{ color: copiedItem ? "#166534" : "#64748b", fontSize: "11px", fontWeight: 700 }}>
          {copiedItem ? `${copiedItem} copied` : "Click any shortcode to copy"}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "8px" }}>
        {items.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => handleCopy(item)}
            style={{
              border: copiedItem === item ? "1px solid rgba(22, 101, 52, 0.22)" : "1px solid rgba(194, 65, 12, 0.12)",
              borderRadius: "8px",
              background: copiedItem === item ? "#dcfce7" : "#fff",
              padding: "6px 8px",
              cursor: "pointer",
              textAlign: "center",
              minHeight: "34px",
            }}
          >
            <code style={{ color: copiedItem === item ? "#166534" : "#7c2d3a", fontWeight: 700, fontSize: "15px", lineHeight: 1.2, whiteSpace: "nowrap", letterSpacing: "0.01em" }}>{item}</code>
            <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, marginTop: "4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {previewValues[shortcodeToKey(item)] || "Preview"}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function PositionButton({ label, onClick }) {
  const iconClass = label === "+" ? "bi-plus-lg" : "bi-dash-lg";
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: "1px solid rgba(148, 163, 184, 0.22)",
        background: "#fff",
        color: "#334155",
        borderRadius: "9px",
        padding: 0,
        width: "34px",
        height: "30px",
        fontWeight: 800,
        fontSize: "12px",
        cursor: "pointer",
        display: "grid",
        placeItems: "center",
      }}
      aria-label={label === "+" ? "Increase position" : "Decrease position"}
    >
      <i className={`bi ${iconClass}`}></i>
    </button>
  );
}

function FieldPositionControls({ fields, activeTab, fieldPositions, onAdjust, onColorChange, layout = "side" }) {
  const isBelow = layout === "below";

  return (
    <div
      style={{
        ...cardStyle,
        display: "grid",
        gridTemplateRows: isBelow ? "auto auto" : "auto 1fr",
        gap: isBelow ? "12px" : "6px",
        alignContent: "start",
        padding: "12px",
        height: isBelow ? "auto" : "100%",
        maxHeight: isBelow ? "none" : "1210px",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
        <h3 style={sectionTitleStyle}>Position Controllers</h3>
        <span style={pillStyle}>{fields.length} fields</span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isBelow ? "repeat(auto-fit, minmax(190px, 1fr))" : "1fr",
          gap: isBelow ? "10px" : "5px",
          overflowY: isBelow ? "visible" : "auto",
          alignContent: "start",
          minHeight: 0,
          maxHeight: isBelow ? "none" : "1155px",
          paddingRight: isBelow ? 0 : "4px",
        }}
      >
        {fields.map((field) => (
          <div key={field.key} style={controllerCardStyle}>
            <div style={{ color: "#0f172a", fontWeight: 800, fontSize: "13px", textAlign: "center" }}>{field.label}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "6px" }}>
              <div style={{ display: "grid", gap: "5px", justifyItems: "center" }}>
                <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, textAlign: "center" }}>Left/Right</div>
                <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                  <PositionButton label="-" onClick={() => onAdjust(field.key, "x", -5)} />
                  <PositionButton label="+" onClick={() => onAdjust(field.key, "x", 5)} />
                </div>
              </div>
              <div style={{ display: "grid", gap: "5px", justifyItems: "center" }}>
                <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, textAlign: "center" }}>Up/Down</div>
                <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                  <PositionButton label="-" onClick={() => onAdjust(field.key, "y", -5)} />
                  <PositionButton label="+" onClick={() => onAdjust(field.key, "y", 5)} />
                </div>
              </div>
            </div>
            <div style={{ display: "grid", gap: "5px", justifyItems: "center" }}>
              <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, textAlign: "center" }}>Text Colour</div>
              <input
                type="color"
                value={fieldPositions?.[activeTab]?.[field.key]?.color || "#0f172a"}
                onChange={(event) => onColorChange(field.key, event.target.value)}
                style={{ width: "44px", height: "30px", border: "1px solid rgba(148, 163, 184, 0.22)", borderRadius: "8px", padding: "2px", background: "#fff" }}
                aria-label={`${field.label} text colour`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FranchisePositionControllers({
  fieldPositions,
  borderStyle,
  onAdjust,
  onColorChange,
  onBorderChange,
  selectedFieldKey,
  setSelectedFieldKey,
  fieldStyleMap,
  onFieldStyleChange,
}) {
  return (
    <div style={{ ...cardStyle, display: "grid", gap: "10px", position: "sticky", top: "84px", width: "100%" }}>
      <h3 style={{ margin: 0, color: "#0f172a", fontSize: "16px" }}>Franchise Position Controllers</h3>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "8px", alignItems: "start", alignContent: "start" }}>
      <div style={{ ...controllerCardStyle, alignContent: "start" }}>
        <div style={{ fontSize: "12px", fontWeight: 800, color: "#0f172a" }}>Outer Border</div>
        <div style={{ display: "grid", gap: "8px" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "8px", color: "#475569", fontSize: "12px", fontWeight: 700 }}>
            <input
              type="checkbox"
              checked={Boolean(borderStyle?.enabled)}
              onChange={(event) => onBorderChange("enabled", event.target.checked)}
            />
            Enable Border
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "8px" }}>
            <div>
              <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, marginBottom: "4px" }}>Width (px)</div>
              <input
                type="number"
                min="0"
                max="20"
                value={Number(borderStyle?.width || 0)}
                onChange={(event) => onBorderChange("width", Number(event.target.value || 0))}
                style={{ ...inputStyle, padding: "8px 10px", borderRadius: "8px" }}
              />
            </div>
            <div>
              <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, marginBottom: "4px" }}>Style</div>
              <select
                value={borderStyle?.style || "solid"}
                onChange={(event) => onBorderChange("style", event.target.value)}
                style={{ ...inputStyle, padding: "8px 10px", borderRadius: "8px" }}
              >
                <option value="solid">Solid</option>
                <option value="dashed">Dashed</option>
                <option value="dotted">Dotted</option>
                <option value="double">Double</option>
              </select>
            </div>
            <div>
              <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, marginBottom: "4px" }}>Radius (px)</div>
              <input
                type="number"
                min="0"
                max="80"
                value={Number(borderStyle?.radius || 0)}
                onChange={(event) => onBorderChange("radius", Number(event.target.value || 0))}
                style={{ ...inputStyle, padding: "8px 10px", borderRadius: "8px" }}
              />
            </div>
            <div>
              <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, marginBottom: "4px" }}>Margin (px)</div>
              <input
                type="number"
                min="0"
                max="120"
                value={Number(borderStyle?.margin ?? borderStyle?.bottomMargin ?? 0)}
                onChange={(event) => onBorderChange("margin", Number(event.target.value || 0))}
                style={{ ...inputStyle, padding: "8px 10px", borderRadius: "8px" }}
              />
            </div>
            <div>
              <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, marginBottom: "4px" }}>Color</div>
              <input
                type="color"
                value={borderStyle?.color || "#0f172a"}
                onChange={(event) => onBorderChange("color", event.target.value)}
                style={{ width: "100%", height: "38px", border: "1px solid #cbd5e1", borderRadius: "8px", background: "#fff" }}
              />
            </div>
          </div>
        </div>
      </div>
      <div style={{ ...controllerCardStyle, alignContent: "start" }}>
        <div style={{ fontSize: "12px", fontWeight: 800, color: "#0f172a" }}>Inner Border</div>
        <div style={{ display: "grid", gap: "8px" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "8px", color: "#475569", fontSize: "12px", fontWeight: 700 }}>
            <input
              type="checkbox"
              checked={Boolean(borderStyle?.innerEnabled)}
              onChange={(event) => onBorderChange("innerEnabled", event.target.checked)}
            />
            Enable Inner Border
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "8px" }}>
            <div>
              <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, marginBottom: "4px" }}>Inner Width (px)</div>
              <input type="number" min="0" max="20" value={Number(borderStyle?.innerWidth || 1)} onChange={(event) => onBorderChange("innerWidth", Number(event.target.value || 0))} style={{ ...inputStyle, padding: "8px 10px", borderRadius: "8px" }} />
            </div>
            <div>
              <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, marginBottom: "4px" }}>Inner Style</div>
              <select value={borderStyle?.innerStyle || "solid"} onChange={(event) => onBorderChange("innerStyle", event.target.value)} style={{ ...inputStyle, padding: "8px 10px", borderRadius: "8px" }}>
                <option value="solid">Solid</option>
                <option value="dashed">Dashed</option>
                <option value="dotted">Dotted</option>
                <option value="double">Double</option>
              </select>
            </div>
            <div>
              <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, marginBottom: "4px" }}>Inner Gap (px)</div>
              <input type="number" min="0" max="120" value={Number(borderStyle?.innerGap || 10)} onChange={(event) => onBorderChange("innerGap", Number(event.target.value || 0))} style={{ ...inputStyle, padding: "8px 10px", borderRadius: "8px" }} />
            </div>
            <div>
              <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, marginBottom: "4px" }}>Inner Radius (px)</div>
              <input type="number" min="0" max="80" value={Number(borderStyle?.innerRadius || 0)} onChange={(event) => onBorderChange("innerRadius", Number(event.target.value || 0))} style={{ ...inputStyle, padding: "8px 10px", borderRadius: "8px" }} />
            </div>
            <div>
              <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, marginBottom: "4px" }}>Color</div>
              <input
                type="color"
                value={borderStyle?.innerColor || "#7c2d3a"}
                onChange={(event) => onBorderChange("innerColor", event.target.value)}
                style={{ width: "100%", height: "38px", border: "1px solid #cbd5e1", borderRadius: "8px", background: "#fff" }}
              />
            </div>
          </div>
        </div>
      </div>
      </div>
      <div style={controllerCardStyle}>
        <h3 style={{ margin: 0, color: "#0f172a", fontSize: "14px" }}>Word Style Controller</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "10px" }}>
          <div>
            <label style={labelStyle}>Select Field</label>
            <select style={inputStyle} value={selectedFieldKey} onChange={(e) => setSelectedFieldKey(e.target.value)}>
              {franchiseAffiliatedTextFields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "minmax(0, 0.8fr) minmax(0, 1.2fr)", gap: "10px" }}>
            <div>
              <label style={labelStyle}>Font Size</label>
              <input type="number" min="10" max="64" style={inputStyle} value={Number((fieldStyleMap?.[selectedFieldKey]?.fontSize) || 20)} onChange={(e) => onFieldStyleChange("fontSize", Number(e.target.value || 20))} />
            </div>
            <div>
              <label style={labelStyle}>Font Family</label>
              <select style={inputStyle} value={fieldStyleMap?.[selectedFieldKey]?.fontFamily || "Georgia, serif"} onChange={(e) => onFieldStyleChange("fontFamily", e.target.value)}>
                <option value="Georgia, serif">Georgia</option>
                <option value="'Times New Roman', serif">Times New Roman</option>
                <option value="Arial, sans-serif">Arial</option>
                <option value="'Trebuchet MS', sans-serif">Trebuchet</option>
              </select>
            </div>
          </div>
          <div style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "10px" }}>
            <div>
              <label style={labelStyle}>Text Align</label>
              <select style={inputStyle} value={fieldStyleMap?.[selectedFieldKey]?.align || "left"} onChange={(e) => onFieldStyleChange("align", e.target.value)}>
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Color</label>
              <input type="color" style={{ ...inputStyle, height: "42px", padding: "4px" }} value={fieldStyleMap?.[selectedFieldKey]?.color || "#0f172a"} onChange={(e) => onFieldStyleChange("color", e.target.value)} />
            </div>
          </div>
          <div style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "10px" }}>
            <div>
              <label style={labelStyle}>Width (px)</label>
              <input type="number" min="40" max="1200" style={inputStyle} value={Number(fieldStyleMap?.[selectedFieldKey]?.width || 0)} onChange={(e) => onFieldStyleChange("width", Number(e.target.value || 0))} />
            </div>
            <div>
              <label style={labelStyle}>Height (px)</label>
              <input type="number" min="0" max="1200" style={inputStyle} value={Number(fieldStyleMap?.[selectedFieldKey]?.height || 0)} onChange={(e) => onFieldStyleChange("height", Number(e.target.value || 0))} />
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "end", flexWrap: "wrap" }}>
            <label><input type="checkbox" checked={Boolean(fieldStyleMap?.[selectedFieldKey]?.bold)} onChange={(e) => onFieldStyleChange("bold", e.target.checked)} /> Bold</label>
            <label><input type="checkbox" checked={Boolean(fieldStyleMap?.[selectedFieldKey]?.italic)} onChange={(e) => onFieldStyleChange("italic", e.target.checked)} /> Italic</label>
            <label><input type="checkbox" checked={Boolean(fieldStyleMap?.[selectedFieldKey]?.underline)} onChange={(e) => onFieldStyleChange("underline", e.target.checked)} /> Underline</label>
          </div>
        </div>
      </div>
    </div>
  );
}

function A4LandscapePreview({ documentType, config, values, previewFields, fieldPositions }) {
  const previewRef = useRef(null);
  const dragRef = useRef({ key: null, offsetX: 0, offsetY: 0, boxW: 120, boxH: 40 });
  const imageDragRef = useRef({ key: null, mode: "move", offsetX: 0, offsetY: 0, startW: 0, startH: 0, startX: 0, startY: 0 });
  const [activePreviewKey, setActivePreviewKey] = useState("");
  const imageLayout = values?.certificateImageLayout || {};
  const isFranchiseAffiliatedMode = Boolean(values?.isFranchiseAffiliatedMode);
  const getUploadPreview = (...keys) => {
    const uploads = values?.uploads || {};
    for (const key of keys) {
      if (key && uploads[key]) return uploads[key];
    }
    return "";
  };
  const backgroundPreview = getUploadPreview(config.uploads?.[0], "Certificate Background", "Header Image");
  const logoPreview = isFranchiseAffiliatedMode ? "" : getUploadPreview(config.uploads?.[1], "Institute Logo");
  const signaturePreview = getUploadPreview(
    config.uploads?.[2],
    "Chairman Signature",
    "NIDCS Administrator Signature"
  );
  const authoritySignaturePreview = getUploadPreview("Franchise Authority Signature");
  const adminSignaturePreview = getUploadPreview("NIDCS Administrator Signature");
  const borderStyle = values.certificateBorderStyle || {};
  const borderPadding = Math.max(0, Number(borderStyle.padding ?? borderStyle.inset ?? 0));
  const borderMargin = Math.max(0, Number(borderStyle.margin ?? borderStyle.bottomMargin ?? 0));
  const innerEnabled = Boolean(borderStyle.innerEnabled);
  const innerWidth = Math.max(0, Number(borderStyle.innerWidth || 0));
  const borderInset = documentType === "certificate" ? borderMargin : 0;
  const innerGap = Math.max(0, Number(borderStyle.innerGap || 0));
  const contentInset = documentType === "certificate"
    ? borderMargin + borderPadding + (innerEnabled ? innerGap + innerWidth : 0)
    : 0;
  const orientation = "Portrait A4";
  const canvasW = Number(a4PreviewStyle(documentType).width.replace("px", "")) - contentInset * 2;
  const canvasH = Number(a4PreviewStyle(documentType).height.replace("px", "")) - contentInset * 2;

  const fieldStyleMap = values?.certificateFieldStyleMap || {};
  const toFiniteNumber = (value, fallback = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
  };
  const buildQrPreviewUrl = (rawValue) => {
    const text = String(rawValue || "").trim();
    if (!text || text.startsWith("{{")) return "";
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=4&data=${encodeURIComponent(text)}`;
  };
  const getDefaultFieldWidth = (key) => {
    if (key === "student_photo" || key === "qr_code") return 118;
    if (key === "body_text") return Number(values?.certificateBodyStyle?.bodyWidth || 760);
    if (key === "title") return documentType === "certificate" ? 700 : 460;
    if (key === "semester_rows" || key === "module_details") return 830;
    return documentType === "certificate" ? 680 : 520;
  };
  const getDefaultFieldHeight = (key) => {
    if (key === "student_photo") return 138;
    if (key === "qr_code") return 118;
    if (key === "body_text") return 160;
    return 40;
  };
  const handleMouseMove = (event) => {
    if (imageDragRef.current.key && previewRef.current) {
      event.preventDefault();
      const rect = previewRef.current.getBoundingClientRect();
      const key = imageDragRef.current.key;
      const current = imageLayout[key] || { x: 0, y: 0, width: 150, height: 58 };
      if (imageDragRef.current.mode === "resize") {
        const nextW = Math.max(60, Math.min(canvasW, Math.round(imageDragRef.current.startW + (event.clientX - imageDragRef.current.startX))));
        const nextH = Math.max(30, Math.min(canvasH, Math.round(imageDragRef.current.startH + (event.clientY - imageDragRef.current.startY))));
        if (typeof values?.onImageLayoutSet === "function") values.onImageLayoutSet(key, { width: nextW, height: nextH });
        return;
      }
      if (imageDragRef.current.mode === "crop-pan") {
        const baseW = Math.max(1, Number(imageDragRef.current.startW || 1));
        const baseH = Math.max(1, Number(imageDragRef.current.startH || 1));
        const dx = event.clientX - imageDragRef.current.startX;
        const dy = event.clientY - imageDragRef.current.startY;
        const nextCropX = Math.max(0, Math.min(100, Number(imageDragRef.current.baseCropX || 50) - ((dx / baseW) * 100)));
        const nextCropY = Math.max(0, Math.min(100, Number(imageDragRef.current.baseCropY || 50) - ((dy / baseH) * 100)));
        if (typeof values?.onImageLayoutSet === "function") values.onImageLayoutSet(key, { cropX: nextCropX, cropY: nextCropY });
        return;
      }
      const nextX = Math.max(0, Math.min(canvasW - Math.max(60, Number(current.width || 150)), Math.round(event.clientX - rect.left - imageDragRef.current.offsetX - contentInset)));
      const nextY = Math.max(0, Math.min(canvasH - Math.max(30, Number(current.height || 58)), Math.round(event.clientY - rect.top - imageDragRef.current.offsetY - contentInset)));
      if (typeof values?.onImageLayoutSet === "function") values.onImageLayoutSet(key, { x: nextX, y: nextY });
      return;
    }
    if (!dragRef.current.key || !previewRef.current) return;
    event.preventDefault();
    const rect = previewRef.current.getBoundingClientRect();
    const key = dragRef.current.key;
    const maxW = Number(dragRef.current.boxW || getDefaultFieldWidth(key));
    const maxH = Number(dragRef.current.boxH || getDefaultFieldHeight(key));
    const nextX = Math.max(0, Math.min(canvasW - Math.max(40, maxW), Math.round(event.clientX - rect.left - dragRef.current.offsetX - contentInset)));
    const nextY = Math.max(0, Math.min(canvasH - Math.max(20, maxH), Math.round(event.clientY - rect.top - dragRef.current.offsetY - contentInset)));
    if (typeof values?.onFieldPositionSet === "function") {
      values.onFieldPositionSet(dragRef.current.key, nextX, nextY);
    }
  };
  const handleMouseUp = () => {
    dragRef.current = { key: null, offsetX: 0, offsetY: 0, boxW: 120, boxH: 40 };
    imageDragRef.current = { key: null, mode: "move", offsetX: 0, offsetY: 0, startW: 0, startH: 0, startX: 0, startY: 0 };
  };

  useEffect(() => {
    const stopDragOnGlobal = () => {
      dragRef.current = { key: null, offsetX: 0, offsetY: 0, boxW: 120, boxH: 40 };
      imageDragRef.current = { key: null, mode: "move", offsetX: 0, offsetY: 0, startW: 0, startH: 0, startX: 0, startY: 0 };
    };
    window.addEventListener("mouseup", stopDragOnGlobal);
    window.addEventListener("blur", stopDragOnGlobal);
    window.addEventListener("scroll", stopDragOnGlobal, true);
    return () => {
      window.removeEventListener("mouseup", stopDragOnGlobal);
      window.removeEventListener("blur", stopDragOnGlobal);
      window.removeEventListener("scroll", stopDragOnGlobal, true);
    };
  }, []);

  return (
    <div style={previewShellStyle}>
      <div style={{ padding: "18px", background: "#f8fafc", display: "grid", gap: "14px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <div>
            <div style={{ color: "#0f172a", fontWeight: 900, fontSize: "17px" }}>{config.title} Preview</div>
          </div>
        </div>
        <div style={{ overflowX: "auto", overflowY: "hidden", padding: "4px 0 8px" }}>
          <div
            ref={previewRef}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{
            ...a4PreviewStyle(documentType),
            margin: "0",
            background: backgroundPreview ? "#fff" : "linear-gradient(135deg, #fffaf5 0%, #ffffff 45%, #f8fafc 100%)",
            boxSizing: "border-box",
            border: "1px solid rgba(148, 163, 184, 0.16)",
            borderRadius: "18px",
            }}
          >
          {documentType === "certificate" && borderStyle.enabled ? (
            <div
              style={{
                position: "absolute",
                inset: `${borderInset}px`,
                border: `${Math.max(0, Number(borderStyle.width || 0))}px ${borderStyle.style || "solid"} ${borderStyle.color || "#0f172a"}`,
                borderRadius: `${Math.max(0, Number(borderStyle.radius || 0))}px`,
                pointerEvents: "none",
                zIndex: 1,
                boxSizing: "border-box",
              }}
            />
          ) : null}
          {documentType === "certificate" && borderStyle.enabled && borderStyle.innerEnabled ? (
            <div
              style={{
                position: "absolute",
                inset: `${borderInset + innerGap}px`,
                border: `${Math.max(0, Number(borderStyle.innerWidth || 0))}px ${borderStyle.innerStyle || "solid"} ${borderStyle.innerColor || "#7c2d3a"}`,
                borderRadius: `${Math.max(0, Number(borderStyle.innerRadius || 0))}px`,
                pointerEvents: "none",
                zIndex: 1,
                boxSizing: "border-box",
              }}
            />
          ) : null}
          {backgroundPreview && !isFranchiseAffiliatedMode ? (
            <img
              src={backgroundPreview}
              alt=""
              style={{
                position: "absolute",
                inset: `${contentInset}px`,
                objectFit: "contain",
                display: "block",
                zIndex: 0,
                pointerEvents: "none",
              }}
            />
          ) : null}
          {backgroundPreview && isFranchiseAffiliatedMode ? (
            (() => {
              const box = imageLayout.headerImage || { x: 150, y: 48, width: 320, height: 110, fit: "contain", cropX: 50, cropY: 50 };
              const safeW = Math.max(80, Math.min(toFiniteNumber(box.width, 320), canvasW));
              const safeH = Math.max(50, Math.min(toFiniteNumber(box.height, 110), canvasH));
              const safeX = Math.max(0, Math.min(canvasW - safeW, toFiniteNumber(box.x, 150)));
              const safeY = Math.max(0, Math.min(canvasH - safeH, toFiniteNumber(box.y, 48)));
              const cropX = Math.max(0, Math.min(100, toFiniteNumber(box.cropX, 50)));
              const cropY = Math.max(0, Math.min(100, toFiniteNumber(box.cropY, 50)));
              return (
                <div
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const rect = event.currentTarget.getBoundingClientRect();
                    const cropMode = box.fit === "cover" && !event.shiftKey;
                    imageDragRef.current = {
                      key: "headerImage",
                      mode: cropMode ? "crop-pan" : "move",
                      offsetX: event.clientX - rect.left,
                      offsetY: event.clientY - rect.top,
                      startW: safeW,
                      startH: safeH,
                      startX: event.clientX,
                      startY: event.clientY,
                      baseCropX: Number(box.cropX ?? 50),
                      baseCropY: Number(box.cropY ?? 50),
                    };
                    setActivePreviewKey("headerImage");
                  }}
                  style={{ position: "absolute", left: `${safeX + contentInset}px`, top: `${safeY + contentInset}px`, width: `${safeW}px`, height: `${safeH}px`, borderRadius: "10px", overflow: "hidden", border: activePreviewKey === "headerImage" ? "2px solid #c2410c" : "none", background: "#fff", cursor: "move", zIndex: 4, boxShadow: activePreviewKey === "headerImage" ? "0 0 0 2px rgba(194,65,12,0.25)" : "none" }}
                >
                  <img src={backgroundPreview} alt="Header" style={{ width: "100%", height: "100%", objectFit: box.fit === "cover" ? "cover" : "contain", objectPosition: `${cropX}% ${cropY}%`, display: "block", background: "#fff" }} />
                  <button
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (typeof values?.onToggleImageFitMode === "function") values.onToggleImageFitMode("headerImage");
                    }}
                    style={{ position: "absolute", top: "6px", right: "6px", border: "1px solid rgba(148, 163, 184, 0.35)", background: "rgba(255,255,255,0.95)", borderRadius: "6px", fontSize: "10px", fontWeight: 800, color: "#334155", padding: "3px 6px", cursor: "pointer" }}
                  >
                    {box.fit === "cover" ? "Crop" : "Fit"}
                  </button>
                  <div
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      imageDragRef.current = { key: "headerImage", mode: "resize", offsetX: 0, offsetY: 0, startW: safeW, startH: safeH, startX: event.clientX, startY: event.clientY };
                    }}
                    style={{ position: "absolute", right: 0, bottom: 0, width: "14px", height: "14px", background: "#7c2d3a", cursor: "nwse-resize" }}
                  />
                </div>
              );
            })()
          ) : null}
          {logoPreview ? (
            <div style={{ position: "absolute", left: `${48 + contentInset}px`, top: `${38 + contentInset}px`, width: "86px", height: "86px", borderRadius: "22px", overflow: "hidden", border: "1px solid rgba(148, 163, 184, 0.18)", background: "#fff", zIndex: 3 }}>
              <img src={logoPreview} alt="Document logo" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
          ) : null}
          {Object.entries(previewFields).map(([key, value]) => {
            if (documentType === "certificate" && key === "verification_qr") return null;
            if (documentType === "marksheet" && key === "title") return null;

            const position = fieldPositions[key];
            if (!position) return null;
            const isTitle = key === "title";
            const isFooter = key === "footer_note";
            const isModuleDetails = key === "module_details";
            const isRows = key === "semester_rows" || isModuleDetails;
            const isPhoto = key === "student_photo";
            const isQrCode = key === "qr_code";
            const isBody = key === "body_text";
            const bodyStyle = values.certificateBodyStyle || {};
            const renderedValue = resolvePreviewShortcodes(value, previewFields);
            const bodyWidth = Number(bodyStyle.bodyWidth || (documentType === "certificate" ? 760 : 560));
            const isMarksheetCentreCode = documentType === "marksheet" && key === "centre_code";

            if (isPhoto) {
              const fieldStyle = fieldStyleMap[key] || {};
              const posX = Number(position.x || 0);
              const posY = Number(position.y || 0);
              const reqW = Number(fieldStyle.width || 118);
              const reqH = Number(fieldStyle.height || 138);
              const safeW = Math.max(40, Math.min(reqW, canvasW));
              const safeH = Math.max(40, Math.min(reqH, canvasH));
              const safeX = Math.max(0, Math.min(canvasW - safeW, posX));
              const safeY = Math.max(0, Math.min(canvasH - safeH, posY));
              return (
                <div
                  key={key}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const rect = event.currentTarget.getBoundingClientRect();
                    dragRef.current = {
                      key,
                      offsetX: event.clientX - rect.left,
                      offsetY: event.clientY - rect.top,
                      boxW: rect.width,
                      boxH: rect.height,
                    };
                    setActivePreviewKey(key);
                  }}
                  style={{
                    position: "absolute",
                    left: `${safeX + contentInset}px`,
                    top: `${safeY + contentInset}px`,
                    width: `${safeW}px`,
                    height: `${safeH}px`,
                    border: activePreviewKey === key ? "2px solid #c2410c" : "none",
                    background: "rgba(255,255,255,0.72)",
                    display: "grid",
                    placeItems: "center",
                    color: "#1e69ad",
                    fontSize: "12px",
                    fontWeight: 800,
                    textAlign: "center",
                    zIndex: 2,
                    overflow: "hidden",
                    boxShadow: activePreviewKey === key ? "0 0 0 2px rgba(194,65,12,0.25)" : "none",
                  }}
                >
                  {String(value || "").startsWith("data:") || String(value || "").startsWith("http") || String(value || "").startsWith("/uploads/")
                    ? <img src={value} alt="Student preview" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block", background: "#fff" }} />
                    : "Student Photo"}
                </div>
              );
            }

            if (isQrCode) {
              const fieldStyle = fieldStyleMap[key] || {};
              const posX = Number(position.x || 0);
              const posY = Number(position.y || 0);
              const reqW = Number(fieldStyle.width || 118);
              const reqH = Number(fieldStyle.height || 118);
              const safeW = Math.max(40, Math.min(reqW, canvasW));
              const safeH = Math.max(40, Math.min(reqH, canvasH));
              const safeX = Math.max(0, Math.min(canvasW - safeW, posX));
              const safeY = Math.max(0, Math.min(canvasH - safeH, posY));
              return (
                <div
                  key={key}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const rect = event.currentTarget.getBoundingClientRect();
                    dragRef.current = {
                      key,
                      offsetX: event.clientX - rect.left,
                      offsetY: event.clientY - rect.top,
                      boxW: rect.width,
                      boxH: rect.height,
                    };
                    setActivePreviewKey(key);
                  }}
                  style={{
                    position: "absolute",
                    left: `${safeX + contentInset}px`,
                    top: `${safeY + contentInset}px`,
                    width: `${safeW}px`,
                    height: `${safeH}px`,
                    border: activePreviewKey === key ? "2px solid #c2410c" : "none",
                    background: "#fff",
                    display: "grid",
                    placeItems: "center",
                    color: "#1e69ad",
                    fontSize: "12px",
                    fontWeight: 800,
                    textAlign: "center",
                    zIndex: 2,
                    overflow: "hidden",
                    boxShadow: activePreviewKey === key ? "0 0 0 2px rgba(194,65,12,0.25)" : "none",
                  }}
                >
                  {buildQrPreviewUrl(value)
                    ? <img src={buildQrPreviewUrl(value)} alt="QR preview" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block", background: "#fff" }} />
                    : "QR Code"}
                </div>
              );
            }

            const fieldStyle = fieldStyleMap[key] || {};
            const posX = Number(position.x || 0);
            const posY = Number(position.y || 0);
            const reqW = Number(fieldStyle.width || (isRows ? 830 : isBody ? bodyWidth : isTitle ? (documentType === "certificate" ? 700 : 460) : (documentType === "certificate" ? 680 : 520)));
            const reqH = Number(fieldStyle.height || 40);
            const safeW = Math.max(40, Math.min(reqW, canvasW));
            const safeH = Math.max(30, Math.min(reqH, canvasH));
            const safeX = Math.max(0, Math.min(canvasW - safeW, posX));
            const safeY = Math.max(0, Math.min(canvasH - safeH, posY));
            return (
              <div
                key={key}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  const rect = event.currentTarget.getBoundingClientRect();
                  dragRef.current = {
                    key,
                    offsetX: event.clientX - rect.left,
                    offsetY: event.clientY - rect.top,
                    boxW: rect.width,
                    boxH: rect.height,
                  };
                  if (typeof values?.onFieldSelect === "function") values.onFieldSelect(key);
                  setActivePreviewKey(key);
                }}
                style={{
                  position: "absolute",
                  left: `${safeX + contentInset}px`,
                  top: `${safeY + contentInset}px`,
                  color: fieldStyle.color || position.color || (isFooter ? "#475569" : "#0f172a"),
                  fontWeight: fieldStyle.bold ? 800 : (isTitle ? 800 : isRows ? 600 : 700),
                  fontStyle: fieldStyle.italic ? "italic" : "normal",
                  textDecoration: fieldStyle.underline ? "underline" : "none",
                  fontSize: `${Number(fieldStyle.fontSize || (isTitle ? 34 : isBody ? (bodyStyle.fontSize || 20) : isFooter ? 16 : isModuleDetails ? 13 : isMarksheetCentreCode ? 14 : 20))}px`,
                  lineHeight: Number(fieldStyle.lineHeight || (isModuleDetails ? 1.45 : isRows || isBody ? 1.8 : 1.5)),
                  whiteSpace: "pre-wrap",
                  maxWidth: `${safeW}px`,
                  width: `${safeW}px`,
                  maxHeight: `${safeH}px`,
                  overflow: "hidden",
                  wordBreak: "break-word",
                  textAlign: fieldStyle.align || (isTitle ? "center" : "left"),
                  letterSpacing: isTitle ? "0.03em" : "normal",
                  fontFamily: fieldStyle.fontFamily || (isBody ? bodyStyle.fontFamily : "inherit"),
                  zIndex: isBody ? 2 : 1,
                  cursor: "move",
                  userSelect: "none",
                  border: activePreviewKey === key ? "2px solid #c2410c" : "none",
                  boxShadow: activePreviewKey === key ? "0 0 0 2px rgba(194,65,12,0.25)" : "none",
                }}
                dangerouslySetInnerHTML={isBody ? { __html: renderedValue } : undefined}
              >
                {isBody ? null : renderedValue}
              </div>
            );
          })}
          {documentType === "certificate" && isFranchiseAffiliatedMode && authoritySignaturePreview ? (
            (() => {
              const box = imageLayout.authoritySignature || { x: 70, y: 746, width: 150, height: 58, fit: "contain", cropX: 50, cropY: 50 };
              const safeW = Math.max(60, Math.min(toFiniteNumber(box.width, 150), canvasW));
              const safeH = Math.max(30, Math.min(toFiniteNumber(box.height, 58), canvasH));
              const safeX = Math.max(0, Math.min(canvasW - safeW, toFiniteNumber(box.x, 70)));
              const safeY = Math.max(0, Math.min(canvasH - safeH, toFiniteNumber(box.y, 746)));
              const cropX = Math.max(0, Math.min(100, toFiniteNumber(box.cropX, 50)));
              const cropY = Math.max(0, Math.min(100, toFiniteNumber(box.cropY, 50)));
              return (
                <div
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const rect = event.currentTarget.getBoundingClientRect();
                    const cropMode = box.fit === "cover" && !event.shiftKey;
                    imageDragRef.current = {
                      key: "authoritySignature",
                      mode: cropMode ? "crop-pan" : "move",
                      offsetX: event.clientX - rect.left,
                      offsetY: event.clientY - rect.top,
                      startW: safeW,
                      startH: safeH,
                      startX: event.clientX,
                      startY: event.clientY,
                      baseCropX: Number(box.cropX ?? 50),
                      baseCropY: Number(box.cropY ?? 50),
                    };
                    setActivePreviewKey("authoritySignature");
                  }}
                  style={{ position: "absolute", left: `${safeX + contentInset}px`, top: `${safeY + contentInset}px`, width: `${safeW}px`, height: `${safeH}px`, borderRadius: "12px", overflow: "hidden", border: activePreviewKey === "authoritySignature" ? "2px solid #c2410c" : "none", background: "#fff", cursor: "move", zIndex: 4, boxShadow: activePreviewKey === "authoritySignature" ? "0 0 0 2px rgba(194,65,12,0.25)" : "none" }}
                >
                  <img src={authoritySignaturePreview} alt="Franchise authority signature" style={{ width: "100%", height: "100%", objectFit: box.fit === "cover" ? "cover" : "contain", objectPosition: `${cropX}% ${cropY}%`, display: "block", background: "#fff" }} />
                  <button
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (typeof values?.onToggleImageFitMode === "function") values.onToggleImageFitMode("authoritySignature");
                    }}
                    style={{ position: "absolute", top: "6px", right: "6px", border: "1px solid rgba(148, 163, 184, 0.35)", background: "rgba(255,255,255,0.95)", borderRadius: "6px", fontSize: "10px", fontWeight: 800, color: "#334155", padding: "3px 6px", cursor: "pointer" }}
                  >
                    {box.fit === "cover" ? "Crop" : "Fit"}
                  </button>
                  <div
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      imageDragRef.current = { key: "authoritySignature", mode: "resize", offsetX: 0, offsetY: 0, startW: safeW, startH: safeH, startX: event.clientX, startY: event.clientY };
                    }}
                    style={{ position: "absolute", right: 0, bottom: 0, width: "14px", height: "14px", background: "#7c2d3a", cursor: "nwse-resize" }}
                  />
                </div>
              );
            })()
          ) : null}
          {documentType === "certificate" && isFranchiseAffiliatedMode && adminSignaturePreview ? (
            (() => {
              const box = imageLayout.adminSignature || { x: 400, y: 746, width: 150, height: 58, fit: "contain", cropX: 50, cropY: 50 };
              const safeW = Math.max(60, Math.min(toFiniteNumber(box.width, 150), canvasW));
              const safeH = Math.max(30, Math.min(toFiniteNumber(box.height, 58), canvasH));
              const safeX = Math.max(0, Math.min(canvasW - safeW, toFiniteNumber(box.x, 400)));
              const safeY = Math.max(0, Math.min(canvasH - safeH, toFiniteNumber(box.y, 746)));
              const cropX = Math.max(0, Math.min(100, toFiniteNumber(box.cropX, 50)));
              const cropY = Math.max(0, Math.min(100, toFiniteNumber(box.cropY, 50)));
              return (
                <div
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const rect = event.currentTarget.getBoundingClientRect();
                    const cropMode = box.fit === "cover" && !event.shiftKey;
                    imageDragRef.current = {
                      key: "adminSignature",
                      mode: cropMode ? "crop-pan" : "move",
                      offsetX: event.clientX - rect.left,
                      offsetY: event.clientY - rect.top,
                      startW: safeW,
                      startH: safeH,
                      startX: event.clientX,
                      startY: event.clientY,
                      baseCropX: Number(box.cropX ?? 50),
                      baseCropY: Number(box.cropY ?? 50),
                    };
                    setActivePreviewKey("adminSignature");
                  }}
                  style={{ position: "absolute", left: `${safeX + contentInset}px`, top: `${safeY + contentInset}px`, width: `${safeW}px`, height: `${safeH}px`, borderRadius: "12px", overflow: "hidden", border: activePreviewKey === "adminSignature" ? "2px solid #c2410c" : "none", background: "#fff", cursor: "move", zIndex: 4, boxShadow: activePreviewKey === "adminSignature" ? "0 0 0 2px rgba(194,65,12,0.25)" : "none" }}
                >
                  <img src={adminSignaturePreview} alt="NIDCS administrator signature" style={{ width: "100%", height: "100%", objectFit: box.fit === "cover" ? "cover" : "contain", objectPosition: `${cropX}% ${cropY}%`, display: "block", background: "#fff" }} />
                  <button
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (typeof values?.onToggleImageFitMode === "function") values.onToggleImageFitMode("adminSignature");
                    }}
                    style={{ position: "absolute", top: "6px", right: "6px", border: "1px solid rgba(148, 163, 184, 0.35)", background: "rgba(255,255,255,0.95)", borderRadius: "6px", fontSize: "10px", fontWeight: 800, color: "#334155", padding: "3px 6px", cursor: "pointer" }}
                  >
                    {box.fit === "cover" ? "Crop" : "Fit"}
                  </button>
                  <div
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      imageDragRef.current = { key: "adminSignature", mode: "resize", offsetX: 0, offsetY: 0, startW: safeW, startH: safeH, startX: event.clientX, startY: event.clientY };
                    }}
                    style={{ position: "absolute", right: 0, bottom: 0, width: "14px", height: "14px", background: "#7c2d3a", cursor: "nwse-resize" }}
                  />
                </div>
              );
            })()
          ) : null}
          {!isFranchiseAffiliatedMode && signaturePreview ? (
            <div style={{ position: "absolute", right: `${68 + contentInset}px`, bottom: `${72 + contentInset}px`, width: "150px", height: "58px", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(148, 163, 184, 0.16)", background: "#fff" }}>
              <img src={signaturePreview} alt="Signature" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
          ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function RichCertificateBodyEditor({ value, styleConfig, onChange, onStyleChange, shortcodes = [] }) {
  const editorRef = useRef(null);

  const runCommand = (command, commandValue = null) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, commandValue);
    onChange(editorRef.current.innerHTML);
  };

  const handleEditorInput = (event) => {
    onChange(event.currentTarget.innerHTML);
  };

  const insertShortcode = (code) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand("insertText", false, code);
    onChange(editorRef.current.innerHTML);
  };

  return (
    <div style={{ ...fieldCardStyle, gap: "14px" }}>
      <div style={{ color: "#0f172a", fontWeight: 800 }}>Certificate Body Text Editor</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "12px" }}>
        <div>
          <label style={labelStyle}>Font Choice</label>
          <select
            style={inputStyle}
            value={styleConfig.fontFamily}
            onChange={(event) => onStyleChange("fontFamily", event.target.value)}
          >
            <option value="Georgia, serif">Georgia</option>
            <option value="'Times New Roman', serif">Times New Roman</option>
            <option value="'Trebuchet MS', sans-serif">Trebuchet</option>
            <option value="Arial, sans-serif">Arial</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Selected Text Size</label>
          <select
            style={inputStyle}
            value={String(styleConfig.fontSize)}
            onChange={(event) => {
              const nextSize = Number(event.target.value);
              onStyleChange("fontSize", nextSize);
              runCommand("fontSize", "7");
              const fonts = editorRef.current?.querySelectorAll("font[size='7']") || [];
              fonts.forEach((node) => {
                node.removeAttribute("size");
                node.style.fontSize = `${nextSize}px`;
              });
              onChange(editorRef.current?.innerHTML || "");
            }}
          >
            {[16, 18, 20, 22, 24, 26, 28, 30, 32].map((size) => (
              <option key={size} value={size}>
                {size}px
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Body Area Width</label>
          <input
            type="range"
            min="360"
            max="1050"
            step="10"
            style={{ width: "100%" }}
            value={Number(styleConfig.bodyWidth || 760)}
            onChange={(event) => onStyleChange("bodyWidth", Number(event.target.value))}
          />
          <div style={{ color: "#64748b", fontSize: "12px", fontWeight: 700, marginTop: "6px" }}>
            {Number(styleConfig.bodyWidth || 760)}px
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        <button
          type="button"
          title="Bold"
          onClick={() => {
            onStyleChange("bold", !styleConfig.bold);
            runCommand("bold");
          }}
          style={formatButtonStyle(styleConfig.bold)}
        >
          <i className="bi bi-type-bold"></i>
        </button>
        <button
          type="button"
          title="Italic"
          onClick={() => {
            onStyleChange("italic", !styleConfig.italic);
            runCommand("italic");
          }}
          style={formatButtonStyle(styleConfig.italic)}
        >
          <i className="bi bi-type-italic"></i>
        </button>
        <button
          type="button"
          title="Underline"
          onClick={() => {
            onStyleChange("underline", !styleConfig.underline);
            runCommand("underline");
          }}
          style={formatButtonStyle(styleConfig.underline)}
        >
          <i className="bi bi-type-underline"></i>
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleEditorInput}
        dangerouslySetInnerHTML={{ __html: value }}
        style={{
          ...textareaStyle,
          minHeight: "160px",
          fontFamily: styleConfig.fontFamily,
          lineHeight: 1.8,
          outline: "none",
        }}
      />
      <div style={{ color: "#64748b", fontSize: "12px", lineHeight: 1.7 }}>
        Text select kore Bold, Italic, Underline, ba Selected Text Size apply korun.
      </div>
      {shortcodes.length ? (
        <div style={{ display: "grid", gap: "8px" }}>
          <div style={{ color: "#0f172a", fontWeight: 700, fontSize: "12px" }}>Body Text Shortcodes</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {shortcodes.map((item) => (
              <button
                key={item}
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => insertShortcode(item)}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function DocumentPreview({ config, values }) {
  const isIdCard = config.uploads.includes("Back Background");
  const backgroundPreview = values.uploads?.[config.uploads[0]] || "";
  const backBackgroundPreview = isIdCard ? values.uploads?.[config.uploads[1]] || "" : "";
  const logoPreview = values.uploads?.[isIdCard ? config.uploads[2] : config.uploads[1]] || "";
  const signaturePreview = isIdCard ? "" : values.uploads?.[config.uploads[2]] || "";

  return (
    <div style={previewShellStyle}>
      <div
        style={{
          background: backgroundPreview ? "#fff" : "linear-gradient(135deg, #fff7ed 0%, #ffffff 42%, #eff6ff 100%)",
          padding: "22px",
          minHeight: "480px",
          display: "grid",
          alignContent: "start",
          gap: "16px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {backgroundPreview ? (
          <img
            src={backgroundPreview}
            alt=""
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", display: "block", zIndex: 0, pointerEvents: "none" }}
          />
        ) : null}
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center", flexWrap: "wrap", position: "relative", zIndex: 1 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "12px" }}>
            {logoPreview ? (
              <div style={{ width: "56px", height: "56px", borderRadius: "18px", overflow: "hidden", border: "1px solid rgba(148, 163, 184, 0.16)", background: "#fff", flexShrink: 0 }}>
                <img src={logoPreview} alt="Logo" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </div>
            ) : (
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "18px",
                  background: config.tone,
                  color: "#7c2d3a",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                  flexShrink: 0,
                }}
              >
                <i className={`bi ${config.icon}`}></i>
              </div>
            )}
            <div>
              <div style={{ color: "#7c2d3a", fontWeight: 800 }}>{values.headerTitle || config.sampleTitle}</div>
              <div style={{ color: "#64748b", fontSize: "12px" }}>{values.subTitle || "Institute-ready sample preview block"}</div>
            </div>
          </div>
          <div style={{ color: "#475569", fontSize: "12px", fontWeight: 700 }}>Live Preview</div>
        </div>

        <div style={{ borderRadius: "22px", border: "1px solid rgba(148, 163, 184, 0.16)", background: "rgba(255,255,255,0.88)", padding: "24px", display: "grid", gap: "16px", position: "relative", zIndex: 1 }}>
          {backBackgroundPreview ? (
            <div style={{ borderRadius: "14px", border: "1px solid rgba(148, 163, 184, 0.14)", background: "#fff", padding: "10px", display: "grid", gap: "8px" }}>
              <div style={{ color: "#64748b", fontSize: "12px", fontWeight: 800 }}>Back Background Preview</div>
              <img src={backBackgroundPreview} alt="Back background" style={{ width: "100%", maxHeight: "220px", objectFit: "contain", display: "block" }} />
            </div>
          ) : null}
          <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: "18px", alignItems: "start" }}>
            <div style={{ borderRadius: "18px", background: "#f8fafc", border: "1px dashed #cbd5e1", minHeight: "180px", display: "grid", placeItems: "center", color: "#94a3b8", fontWeight: 700, overflow: "hidden" }}>
              Photo / Auto QR / Asset
            </div>
            <div style={{ display: "grid", gap: "12px" }}>
              <div style={{ fontSize: "28px", fontWeight: 800, color: "#0f172a" }}>{values.studentNameCode || "{{student_name}}"}</div>
              <div style={{ color: "#475569", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                {values.bodyText || "Sample content area where shortcodes and live fetched data will render instantly in the preview."}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "10px" }}>
                {config.shortcodes.slice(0, 6).map((item) => (
                  <div key={item} style={{ borderRadius: "14px", background: "#f8fafc", padding: "12px", border: "1px solid rgba(148, 163, 184, 0.12)" }}>
                    <div style={{ color: "#7c2d3a", fontSize: "12px", fontWeight: 700 }}>{item}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div style={{ borderTop: "1px solid rgba(148, 163, 184, 0.12)", paddingTop: "14px", display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
            <div style={{ color: "#64748b", fontSize: "12px" }}>{values.footerNote || "Footer note, serial number, QR, and verification line will render here."}</div>
            {signaturePreview ? (
              <div style={{ width: "110px", height: "42px", borderRadius: "10px", overflow: "hidden", border: "1px solid rgba(148, 163, 184, 0.16)", background: "#fff" }}>
                <img src={signaturePreview} alt="Signature" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </div>
            ) : (
              <div style={{ color: "#64748b", fontSize: "12px" }}>Controller-ready preview shell</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AMCIDesignPage() {
  const location = useLocation();
  const isFranchiseAffiliatedMode = location.pathname.includes("/dashboard/franchise-affiliated-cert-design");
  const [activeTab, setActiveTab] = useState(isFranchiseAffiliatedMode ? "certificate" : "admit");
  const [form, setForm] = useState(createDefaultFormState);
  const [previewFields, setPreviewFields] = useState(() => deepClone(initialPreviewFieldValues));
  const [fieldPositions, setFieldPositions] = useState(() => deepClone(initialPreviewFieldPositions));
  const [loadingDesign, setLoadingDesign] = useState(false);
  const [savingDesign, setSavingDesign] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [selectedFieldKey, setSelectedFieldKey] = useState("title");
  const storageDocumentType = isFranchiseAffiliatedMode ? `franchise_affiliated_${activeTab}` : activeTab;
  const visibleTabs = isFranchiseAffiliatedMode ? { certificate: docConfig.certificate } : docConfig;

  const config = docConfig[activeTab];
  const isStructuredPreview = activeTab === "marksheet" || activeTab === "certificate";

  useEffect(() => {
    if (isFranchiseAffiliatedMode && activeTab !== "certificate") {
      setActiveTab("certificate");
    }
  }, [isFranchiseAffiliatedMode, activeTab]);

  useEffect(() => {
    let active = true;

    const loadDesign = async () => {
      try {
        setLoadingDesign(true);
        setSaveMessage("");
        const res = await axios.get(`${API}/amci-designs/${storageDocumentType}`);
        if (!active) return;

        setForm({
          ...createDefaultFormState(),
          ...(res.data?.form || {}),
          certificateBodyStyle: {
            ...createDefaultFormState().certificateBodyStyle,
            ...(res.data?.form?.certificateBodyStyle || {}),
          },
          certificateBorderStyle: {
            ...createDefaultFormState().certificateBorderStyle,
            ...(res.data?.form?.certificateBorderStyle || {}),
            padding:
              res.data?.form?.certificateBorderStyle?.padding ??
              res.data?.form?.certificateBorderStyle?.inset ??
              createDefaultFormState().certificateBorderStyle.padding,
            margin:
              res.data?.form?.certificateBorderStyle?.margin ??
              res.data?.form?.certificateBorderStyle?.bottomMargin ??
              createDefaultFormState().certificateBorderStyle.margin,
          },
          uploads: res.data?.form?.uploads || {},
        });

        if (isFranchiseAffiliatedMode && !res.data?.form) {
          setForm({
            ...createDefaultFormState(),
            headerTitle: "Franchise Affiliation Certificate",
            subTitle: "",
            bodyText: "",
            footerNote: "",
            uploads: {},
          });
        }

        if (isStructuredPreview) {
          setPreviewFields((prev) => ({
            ...prev,
            [activeTab]: {
              ...deepClone(initialPreviewFieldValues[activeTab]),
              ...removeHiddenStructuredFields(activeTab, res.data?.preview_fields || {}),
            },
          }));
          setFieldPositions((prev) => ({
            ...prev,
            [activeTab]: {
              ...deepClone(initialPreviewFieldPositions[activeTab]),
              ...removeHiddenStructuredFields(activeTab, res.data?.field_positions || {}),
            },
          }));

          if (isFranchiseAffiliatedMode && !res.data?.preview_fields && activeTab === "certificate") {
            setPreviewFields((prev) => ({
              ...prev,
              certificate: {
                ...deepClone(initialPreviewFieldValues.certificate),
              },
            }));
          }
        }
      } catch (error) {
        if (!active) return;
        setForm(createDefaultFormState());
        if (isStructuredPreview) {
          setPreviewFields((prev) => ({
            ...prev,
            [activeTab]: deepClone(initialPreviewFieldValues[activeTab]),
          }));
          setFieldPositions((prev) => ({
            ...prev,
            [activeTab]: deepClone(initialPreviewFieldPositions[activeTab]),
          }));
          if (isFranchiseAffiliatedMode && activeTab === "certificate") {
            setPreviewFields((prev) => ({
              ...prev,
              certificate: {
                ...deepClone(initialPreviewFieldValues.certificate),
              },
            }));
          }
        }
      } finally {
        if (active) {
          setLoadingDesign(false);
        }
      }
    };

    loadDesign();

    return () => {
      active = false;
    };
  }, [activeTab, isStructuredPreview, storageDocumentType]);

  const handleUploadChange = async (label, file) => {
    if (!file) return;
    try {
      setSaveMessage(isBackgroundUpload(label) ? "Loading original background image..." : "Optimizing image for safe save...");
      const preview = await compressImageFile(file, label);
      setForm((prev) => ({
        ...prev,
        uploads: {
          ...prev.uploads,
          [label]: preview,
        },
      }));
      setSaveMessage(isBackgroundUpload(label) ? "Original background image ready." : "Image optimized and ready.");
    } catch (error) {
      setSaveMessage("Image upload failed. Please upload a valid PNG/JPG/WEBP file.");
    }
  };

  const handlePreviewFieldChange = (key, value) => {
    setPreviewFields((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [key]: value,
      },
    }));
  };

  const handlePositionAdjust = (key, axis, delta) => {
    setFieldPositions((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [key]: {
          ...(prev[activeTab][key] || { x: 0, y: 0 }),
          [axis]: Math.max(0, Number((prev[activeTab][key] || { x: 0, y: 0 })[axis] || 0) + delta),
        },
      },
    }));
  };

  const handleFieldColorChange = (key, color) => {
    setFieldPositions((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [key]: {
          ...(prev[activeTab][key] || { x: 0, y: 0 }),
          color,
        },
      },
    }));
  };

  const handleCertificateBodyStyleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      certificateBodyStyle: {
        ...prev.certificateBodyStyle,
        [key]: value,
      },
    }));
  };

  const handleCertificateBorderStyleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      certificateBorderStyle: {
        ...createDefaultFormState().certificateBorderStyle,
        ...(prev.certificateBorderStyle || {}),
        [key]: value,
      },
    }));
  };

  const handleFieldPositionSet = (key, x, y) => {
    setFieldPositions((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [key]: {
          ...(prev[activeTab][key] || { x: 0, y: 0 }),
          x: Math.max(0, Number(x || 0)),
          y: Math.max(0, Number(y || 0)),
        },
      },
    }));
  };

  const handleFieldStyleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      certificateFieldStyleMap: {
        ...(prev.certificateFieldStyleMap || {}),
        [selectedFieldKey]: {
          ...((prev.certificateFieldStyleMap || {})[selectedFieldKey] || {}),
          [key]: value,
        },
      },
    }));
  };

  const handleFieldStyleSet = (fieldKey, patch) => {
    setForm((prev) => ({
      ...prev,
      certificateFieldStyleMap: {
        ...(prev.certificateFieldStyleMap || {}),
        [fieldKey]: {
          ...((prev.certificateFieldStyleMap || {})[fieldKey] || {}),
          ...(patch || {}),
        },
      },
    }));
  };

  const handleImageLayoutSet = (imageKey, patch) => {
    setForm((prev) => ({
      ...prev,
      certificateImageLayout: {
        ...(prev.certificateImageLayout || {}),
        [imageKey]: {
          ...((prev.certificateImageLayout || {})[imageKey] || {}),
          ...(patch || {}),
        },
      },
    }));
  };

  const toggleImageFitMode = (imageKey) => {
    setForm((prev) => {
      const current = (prev.certificateImageLayout || {})[imageKey] || {};
      const nextFit = current.fit === "cover" ? "contain" : "cover";
      return {
        ...prev,
        certificateImageLayout: {
          ...(prev.certificateImageLayout || {}),
          [imageKey]: {
            ...current,
            fit: nextFit,
          },
        },
      };
    });
  };

  const saveActiveDesign = async () => {
    try {
      setSavingDesign(true);
      setSaveMessage("");
      const optimizedUploads = await compressUploads(form.uploads || {});
      const optimizedForm = {
        ...form,
        uploads: optimizedUploads,
      };
      const payload = {
        form: optimizedForm,
        preview_fields: isStructuredPreview ? removeHiddenStructuredFields(activeTab, previewFields[activeTab]) : null,
        field_positions: isStructuredPreview ? removeHiddenStructuredFields(activeTab, fieldPositions[activeTab]) : null,
        is_active: true,
      };
      const res = await axios.post(`${API}/amci-designs/${storageDocumentType}`, payload);
      setForm(optimizedForm);
      setSaveMessage(res.data?.message || "Design saved successfully");
    } catch (error) {
      const message = error.response?.data?.message || "Design save failed";
      setSaveMessage(message.includes("max_allowed_packet") ? "Design image was too large. Please re-upload the background once and save again." : message);
    } finally {
      setSavingDesign(false);
    }
  };

  return (
    <DashboardLayout>
      <div style={pageStyle}>
        <section style={heroStyle}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "22px", alignItems: "end" }}>
            <div>
              <p style={{ margin: 0, fontSize: "12px", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.78 }}>
                {isFranchiseAffiliatedMode ? "Franchise Workspace" : "Certification Workspace"}
              </p>
              <h1 style={{ margin: "10px 0 12px", fontSize: "34px", lineHeight: 1.1 }}>
                {isFranchiseAffiliatedMode ? "Franchise Affiliated Cert Design" : "AMCI Design"}
              </h1>
              <p style={{ margin: 0, color: "rgba(255,255,255,0.84)", lineHeight: 1.75, maxWidth: "860px" }}>
                {isFranchiseAffiliatedMode
                  ? "Approved franchise certificate download-er jonno dedicated certificate design ekhane set hobe."
                  : "Admit card, marksheet, certificate, and ID card-er active print design ekhan theke set hobe. Upload, shortcode, live preview, position controller, and final save ek page-e ready."}
              </p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(140px, 1fr))", gap: "10px" }}>
              <div style={heroMetricStyle}>
                <div style={{ fontSize: "22px", fontWeight: 900 }}>{isFranchiseAffiliatedMode ? "1" : "4"}</div>
                <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.78)", fontWeight: 700 }}>document types</div>
              </div>
              <div style={heroMetricStyle}>
                <div style={{ fontSize: "22px", fontWeight: 900 }}>Live</div>
                <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.78)", fontWeight: 700 }}>preview panel</div>
              </div>
            </div>
          </div>
        </section>

        <section style={{ ...cardStyle, display: "grid", gap: "16px", position: "sticky", top: "76px", zIndex: 40 }}>
          <div style={tabWrapStyle}>
            {Object.entries(visibleTabs).map(([key, item]) => (
              <TabButton key={key} active={activeTab === key} icon={item.icon} label={item.title} onClick={() => setActiveTab(key)} />
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ color: "#64748b", fontSize: "13px", lineHeight: 1.7 }}>
              {loadingDesign ? "Loading saved AMCI design..." : "Master admin এই tab-এ যে design save করবে, সেটাই printing-এর active design হবে।"}
            </div>
            <button type="button" className="btn btn-primary" onClick={saveActiveDesign} disabled={savingDesign || loadingDesign}>
              <i className={`bi ${savingDesign ? "bi-hourglass-split" : "bi-save2"} me-2`}></i>
              {savingDesign ? "Saving..." : "Save Active Design"}
            </button>
          </div>
          {saveMessage ? (
            <div style={{ color: saveMessage.toLowerCase().includes("failed") ? "#b91c1c" : "#166534", fontWeight: 700 }}>
              {saveMessage}
            </div>
          ) : null}
          {config.description ? <div style={{ color: "#64748b", lineHeight: 1.7 }}>{config.description}</div> : null}
        </section>

        <section style={{ display: "grid", gap: "18px" }}>
          {!isFranchiseAffiliatedMode ? <ShortcodeList items={config.shortcodes} /> : null}

          {isStructuredPreview ? (
            <section
              style={{
                display: "grid",
                gridTemplateColumns:
                  activeTab === "certificate"
                    ? (isFranchiseAffiliatedMode ? "max-content minmax(0, 1fr)" : "1fr")
                    : "minmax(0, 880px) minmax(220px, 260px)",
                gap: "2px",
                alignItems: activeTab === "certificate" ? "stretch" : "start",
                justifyContent: isFranchiseAffiliatedMode ? "start" : "stretch",
                minWidth: 0,
                overflowX: "hidden",
                position: "relative",
                zIndex: 1,
              }}
            >
              <A4LandscapePreview
                documentType={activeTab}
                config={config}
                values={{
                  ...form,
                  isFranchiseAffiliatedMode,
                  onFieldPositionSet: handleFieldPositionSet,
                  onFieldStyleSet: handleFieldStyleSet,
                  onImageLayoutSet: handleImageLayoutSet,
                  onToggleImageFitMode: toggleImageFitMode,
                  onFieldSelect: setSelectedFieldKey,
                }}
                previewFields={previewFields[activeTab]}
                fieldPositions={fieldPositions[activeTab]}
              />
              {isFranchiseAffiliatedMode ? (
                <FranchisePositionControllers
                  fieldPositions={fieldPositions}
                  borderStyle={form.certificateBorderStyle}
                  onAdjust={handlePositionAdjust}
                  onColorChange={handleFieldColorChange}
                  onBorderChange={handleCertificateBorderStyleChange}
                  selectedFieldKey={selectedFieldKey}
                  setSelectedFieldKey={setSelectedFieldKey}
                  fieldStyleMap={form.certificateFieldStyleMap || {}}
                  onFieldStyleChange={handleFieldStyleChange}
                />
              ) : (
                <FieldPositionControls
                  fields={adjustableFieldConfig[activeTab]}
                  activeTab={activeTab}
                  fieldPositions={fieldPositions}
                  onAdjust={handlePositionAdjust}
                  onColorChange={handleFieldColorChange}
                  layout={activeTab === "certificate" ? "below" : "side"}
                />
              )}
            </section>
          ) : (
            <DocumentPreview config={config} values={form} />
          )}

          {!isFranchiseAffiliatedMode ? (
            <div style={{ ...cardStyle, display: "grid", gap: "16px" }}>
              <h3 style={sectionTitleStyle}>{config.title} Controller Panel</h3>

            {!isStructuredPreview ? (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px" }}>
                  <div>
                    <label style={labelStyle}>Header Title</label>
                    <input style={inputStyle} value={form.headerTitle} onChange={(event) => setForm((prev) => ({ ...prev, headerTitle: event.target.value }))} placeholder={config.sampleTitle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Sub Title / Tagline</label>
                    <input style={inputStyle} value={form.subTitle} onChange={(event) => setForm((prev) => ({ ...prev, subTitle: event.target.value }))} placeholder="Institute-ready printable template" />
                  </div>
                  <div>
                    <label style={labelStyle}>Primary Name Shortcode</label>
                    <input style={inputStyle} value={form.studentNameCode} onChange={(event) => setForm((prev) => ({ ...prev, studentNameCode: event.target.value }))} placeholder="{{student_name}}" />
                  </div>
                  <div>
                    <label style={labelStyle}>Footer Note</label>
                    <input style={inputStyle} value={form.footerNote} onChange={(event) => setForm((prev) => ({ ...prev, footerNote: event.target.value }))} placeholder="Verification and print note" />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Body Content / Template Note</label>
                  <textarea
                    style={textareaStyle}
                    value={form.bodyText}
                    onChange={(event) => setForm((prev) => ({ ...prev, bodyText: event.target.value }))}
                    placeholder="Sample content area jekhane text block, shortcodes, institute note, or document body rules define korben."
                  />
                </div>
              </>
            ) : null}

            {isStructuredPreview ? (
              <div style={{ display: "grid", gap: "14px" }}>
                {activeTab === "certificate" ? (
                  <RichCertificateBodyEditor
                    value={previewFields.certificate.body_text}
                    styleConfig={form.certificateBodyStyle}
                    onChange={(value) => handlePreviewFieldChange("body_text", value)}
                    onStyleChange={handleCertificateBodyStyleChange}
                    shortcodes={(config.shortcodes || []).filter((code) =>
                      [
                        "{{student_name}}",
                        "{{father_name}}",
                        "{{registration_no}}",
                        "{{course_name}}",
                        "{{period_of_course}}",
                        "{{admission_date}}",
                        "{{course_completion_date}}",
                        "{{centre_name}}",
                        "{{centre_code}}",
                        "{{centre_address}}",
                        "{{grade}}",
                        "{{issue_date}}",
                      ].includes(code)
                    )}
                  />
                ) : null}
                {!isFranchiseAffiliatedMode ? (
                  <div style={{ display: "grid", gap: "12px" }}>
                    {adjustableFieldConfig[activeTab].filter((field) => !(activeTab === "certificate" && field.key === "body_text")).map((field) => (
                      <div key={field.key} style={fieldCardStyle}>
                        <div style={{ color: "#0f172a", fontWeight: 700 }}>{field.label}</div>
                        {["semester_rows", "module_details", "footer_note"].includes(field.key) ? (
                          <textarea
                            style={{ ...textareaStyle, minHeight: ["semester_rows", "module_details"].includes(field.key) ? "92px" : "72px" }}
                            value={previewFields[activeTab][field.key] || ""}
                            onChange={(event) => handlePreviewFieldChange(field.key, event.target.value)}
                            placeholder={field.placeholder}
                          />
                        ) : (
                          <input
                            style={inputStyle}
                            value={previewFields[activeTab][field.key] || ""}
                            onChange={(event) => handlePreviewFieldChange(field.key, event.target.value)}
                            placeholder={field.placeholder}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
            </div>
          ) : null}
          {isFranchiseAffiliatedMode && activeTab === "certificate" ? (
            <div style={{ ...cardStyle, display: "grid", gap: "12px" }}>
              <ShortcodeList items={franchiseAffiliatedShortcodes} />
              <h3 style={sectionTitleStyle}>Franchise Certificate Text Fields</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "10px" }}>
                {franchiseAffiliatedTextFields.map((field) => (
                  <div key={field.key} style={fieldCardStyle}>
                    <div style={{ color: "#0f172a", fontWeight: 700 }}>{field.label}</div>
                    <input
                      style={inputStyle}
                      value={previewFields.certificate[field.key] || ""}
                      onChange={(event) => handlePreviewFieldChange(field.key, event.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div style={{ ...cardStyle, display: "grid", gap: "16px" }}>
            <h3 style={sectionTitleStyle}>Upload and Adjustment Tools</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px" }}>
              {(isFranchiseAffiliatedMode ? franchiseAffiliatedUploadLabels : config.uploads).map((item) => (
                <UploadTool
                  key={item}
                  label={item}
                  preview={form.uploads[item]}
                  onChange={(event) => handleUploadChange(item, event.target.files?.[0] || null)}
                />
              ))}
            </div>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}

export default AMCIDesignPage;




